<script setup>
import { ref, watch } from "vue";
import IconExpertShield from "~/svg/icon-expert-shield-inverse.svg";

// Editorial cover art for expert lists owned by Gift Heroes (GH-1299, Figma
// 520-350): a navy-to-purple field with a purple slab cut diagonally across
// the right, and the list's photo set into the slab as an inset panel. Fills
// whatever box it is placed in — the list page masthead, the expert cards,
// and the small tiles on the profile list cards all share it.
const props = defineProps({
    // Plain image URL for the inset panel; null renders the field + slab only.
    image: {
        type: String,
        default: null,
    },
    // CSS object-position for the panel image (the masthead's focal point).
    imagePosition: {
        type: String,
        default: "center",
    },
    // Where the photo panel sits:
    //  banner — the comp's proportions (right 37%, 23px-of-320 inset)
    //  card   — right half, for the 271×186 expert card cover
    //  tile   — fills the box with an even inset, for the narrow list-card tiles
    layout: {
        type: String,
        default: "banner",
        validator: (value) => ["banner", "card", "tile"].includes(value),
    },
    // Shield + "Expert List" mark in the top-left corner (cards; the masthead
    // prints its own eyebrow in the text column).
    eyebrow: {
        type: Boolean,
        default: false,
    },
});

const PANEL_CLASSES = {
    banner: "left-[61.2%] top-[7.2%] right-[2%] bottom-[7.5%]",
    card: "left-[42%] top-[8%] right-[6%] bottom-[8%]",
    tile: "inset-[8%]",
};

// A 404 on the panel image leaves the art alone rather than a broken icon.
const imageFailed = ref(false);
watch(
    () => props.image,
    () => {
        imageFailed.value = false;
    },
);
</script>

<template>
    <div
        class="relative size-full overflow-hidden"
        style="
            background-image: linear-gradient(
                16.46deg,
                #01123c 30.5%,
                #4e00a0 103.66%
            );
        "
        data-test="expert-list-cover"
    >
        <div
            class="absolute inset-0 [clip-path:polygon(55.4%_0,100%_0,100%_100%,71.2%_100%)]"
            style="background-image: linear-gradient(180deg, #9a00f3, #3a0990)"
            aria-hidden="true"
        ></div>

        <div
            v-if="image && !imageFailed"
            class="absolute overflow-hidden rounded-[2px] bg-white"
            :class="PANEL_CLASSES[layout]"
            data-test="expert-list-cover-photo"
        >
            <img
                :src="image"
                alt=""
                class="size-full object-cover"
                :style="{ objectPosition: imagePosition }"
                @error="imageFailed = true"
            />
        </div>

        <span
            v-if="eyebrow"
            class="absolute left-[6%] top-[9%] inline-flex items-center gap-1.5 font-brand-display text-[14px] font-bold italic uppercase leading-none text-[#ffb545]"
            data-test="expert-list-cover-eyebrow"
        >
            <IconExpertShield
                class="h-[17px] w-[14px] shrink-0"
                aria-hidden="true"
            />
            Expert List
        </span>
    </div>
</template>
